import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PricingTiers() {
  const [plans, setPlans] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const { lang, t } = useI18n();
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const subscribe = async (planId) => {
    setLoadingId(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur lors du paiement" : "Payment error");
      setLoadingId(null);
    }
  };

  return (
    <div>
      <div id="plans" className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-tiers">
        {plans.map((plan) => {
          const isPremium = plan.id === "premium";
          return (
            <div
              key={plan.id}
              className={`rounded-3xl p-10 border transition-all duration-500 ${
                isPremium
                  ? "bg-[#1d1d1f] text-white border-transparent"
                  : "bg-white border-black/5 hover:shadow-[0_20px_48px_rgba(0,0,0,0.06)]"
              }`}
              data-testid={`plan-${plan.id}`}
            >
              <div className={`text-[12px] uppercase tracking-[0.2em] mb-6 ${isPremium ? "text-white/60" : "text-[#86868b]"}`}>
                {plan.name}
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-[40px] sm:text-[48px] font-semibold tracking-tight">
                  {format(plan.price)}
                </span>
              </div>
              <div className={`text-[13px] mb-8 ${isPremium ? "text-white/60" : "text-[#86868b]"}`}>
                /{lang === "fr" ? "mois" : "mo"}
              </div>
              <ul className="space-y-3 mb-10">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px]">
                    <Check size={16} strokeWidth={1.8} className={isPremium ? "text-white/80 mt-0.5" : "text-[#1d1d1f] mt-0.5"} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(plan.id)}
                disabled={loadingId === plan.id}
                className={`w-full btn-pill text-[14px] font-medium flex items-center justify-center gap-2 disabled:opacity-60 ${
                  isPremium ? "bg-white text-[#1d1d1f] hover:bg-white/90" : "btn-primary"
                }`}
                data-testid={`plan-cta-${plan.id}`}
              >
                {loadingId === plan.id && <Loader2 size={14} className="animate-spin" />}
                {lang === "fr" ? "Souscrire" : "Subscribe"} {plan.name}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-10">
        <Link to="/devenir-agence" className="text-[13px] text-[#86868b] hover:text-[#1d1d1f] underline underline-offset-4" data-testid="become-agency-link">
          {lang === "fr" ? "Postuler comme agence partenaire →" : "Apply as partner agency →"}
        </Link>
      </div>
    </div>
  );
}
